import { useEffect, useState } from "react";
import { Download, Check } from "lucide-react";
import { jsPDF } from "jspdf";
import { loadBrand, brandPages, PAGE_TOP, PAGE_BOTTOM } from "@/lib/pdfBrand";
import { TYPES, GROUPS } from "@/lib/requirements";
import { useAuth } from "@/context/AuthContext";

const KEY = "pf_what_you_need_v1";
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)) || { types: ["grant"], ticks: {} }; } catch { return { types: ["grant"], ticks: {} }; } };

const buildPdf = async (brand, groups, ticks) => {
  const logos = await loadBrand();
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 18;
  let y = PAGE_TOP + 2;
  const page = (need) => { if (y + need > PAGE_BOTTOM) { doc.addPage(); y = PAGE_TOP; } };
  doc.setFont("helvetica", "bold").setFontSize(18).setTextColor(30, 42, 51).text("What you'll need to apply", M, y);
  y += 7;
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(100).text(`${brand} · ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`, M, y);
  const total = groups.reduce((n, [, [, items]]) => n + items.length, 0);
  const done = groups.reduce((n, [, [, items]]) => n + items.filter(([id]) => ticks[id]).length, 0);
  y += 5;
  doc.text(`${done} of ${total} ready`, M, y);
  y += 8;
  groups.forEach(([, [title, items]]) => {
    page(16);
    doc.setFont("helvetica", "bold").setFontSize(12).setTextColor(31, 138, 139).text(title, M, y);
    y += 6;
    items.forEach(([id, label]) => {
      const lines = doc.setFont("helvetica", "normal").setFontSize(10).splitTextToSize(label, W - M * 2 - 9);
      page(lines.length * 5 + 2);
      doc.setDrawColor(120).setLineWidth(0.3).rect(M, y - 3.6, 4.2, 4.2);
      if (ticks[id]) { doc.setDrawColor(31, 138, 139).setLineWidth(0.8).line(M + 0.8, y - 1.4, M + 1.8, y - 0.2).line(M + 1.8, y - 0.2, M + 3.6, y - 2.9); }
      doc.setTextColor(ticks[id] ? 120 : 30).text(lines, M + 8, y);
      y += lines.length * 5 + 2;
    });
    y += 4;
  });
  page(16);
  doc.setFontSize(8).setTextColor(120).text(doc.splitTextToSize("This checklist helps you prepare. It is not legal advice, and each funder's or the UK IPO's official requirements take priority. Fees are standard online fees and may change; check GOV.UK before paying.", W - M * 2), M, y);
  brandPages(doc, logos, "What you'll need to apply");
  doc.save("what-you-need-checklist.pdf");
};

export default function WhatYouNeed() {
  const { settings } = useAuth();
  const [s, setS] = useState(load);
  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(s));
  }, [s]);
  const groups = [["common", GROUPS.common], ...s.types.map((t) => [t, GROUPS[t]])];
  const total = groups.reduce((n, [, [, items]]) => n + items.length, 0);
  const done = groups.reduce((n, [, [, items]]) => n + items.filter(([id]) => s.ticks[id]).length, 0);
  const toggleType = (t) => setS({ ...s, types: s.types.includes(t) ? s.types.filter((x) => x !== t) : TYPES.map(([k]) => k).filter((k) => k === t || s.types.includes(k)) });
  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8" data-testid="what-you-need-page">
      <p className="caption text-[#0D9488]">Before you start</p>
      <h1 className="mt-3 text-4xl font-bold text-[#0B192C] sm:text-5xl">What you'll need to apply</h1>
      <p className="mt-4 max-w-2xl text-slate-600">Choose what you're applying for, then tick off what you already have. Your ticks are saved in this browser, and you can download the list as a PDF to work through.</p>
      <section className="card mt-10 p-6">
        <h2 className="font-semibold text-[#0B192C]">What are you applying for?</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {TYPES.map(([k, l]) => {
            const on = s.types.includes(k);
            return <button key={k} onClick={() => toggleType(k)} aria-pressed={on} data-testid={`need-type-${k}`} className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${on ? "border-[#1F8A8B] bg-teal-50 text-teal-900" : "border-slate-200 hover:bg-slate-50"}`}>{on && <Check className="h-3.5 w-3.5" />}{l}</button>;
          })}
        </div>
      </section>
      <div className="sticky top-16 z-10 mt-6 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white/95 p-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <p className="text-sm font-semibold text-[#0B192C]" data-testid="need-progress">{done} of {total} ready</p>
          <div className="mt-1.5 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-[#1F8A8B] transition-[width]" style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>
        </div>
        <button onClick={() => buildPdf(settings.platform_name, groups, s.ticks)} data-testid="need-download-pdf" className="btn-teal btn-sm"><Download className="h-4 w-4" />Download PDF</button>
      </div>
      <div className="mt-6 space-y-5">
        {groups.map(([k, [title, items]]) => (
          <section key={k} className="card p-6" data-testid={`need-group-${k}`}>
            <h2 className="font-semibold text-[#0B192C]">{title}</h2>
            <ul className="mt-3 space-y-2.5">
              {items.map(([id, label]) => (
                <li key={id}>
                  <label className="flex cursor-pointer items-start gap-3 text-sm">
                    <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#1F8A8B]" checked={!!s.ticks[id]} onChange={(e) => setS({ ...s, ticks: { ...s.ticks, [id]: e.target.checked } })} data-testid={`need-item-${id}`} />
                    <span className={s.ticks[id] ? "text-slate-500 line-through" : "text-slate-800"}>{label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <p className="mt-6 text-xs text-slate-500">This checklist helps you prepare. It is not legal advice, and each funder's or the UK IPO's official requirements take priority.</p>
    </div>
  );
}
